/**
 * ForecastPageDialogs — extraído de ForecastPage.tsx (P7 split).
 *
 * Renderiza los 5 diálogos de la página:
 *   - InventoryDetailsDialog
 *   - SalesDetailsDialog
 *   - ProductDetailDrawer (rediseño 2026-05-21, ex ProductDetailDialog)
 *   - SelectedProductsDialog (calc dialog)
 *   - Dialog de confirmación activar/desactivar forecast
 *
 * Recibe props desde el padre con todo el state/handlers necesarios.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import InventoryDetailsDialog from "../../components/InventoryDetailsDialog";
import SalesDetailsDialog from "../../components/Forecast/SalesDetailsDialog";
import { ProductDetailDrawer } from "../../components/Forecast/product-detail/ProductDetailDrawer";
import SelectedProductsDialog, {
  type CalcResult,
  type SamplesByProduct,
} from "../../components/Forecast/SelectedProductsDialog";
import { SelectedProduct } from "../../components/Forecast/SelectedProducts.types";

export interface ForecastPageDialogsProps {
  inventoryDialogOpen: boolean;
  salesDialogOpen: boolean;
  detailDialogOpen: boolean;
  calcDialogOpen: boolean;
  confirmDialogOpen: boolean;
  selectedProductId: string | null;
  selectedProductName: string | undefined;
  setInventoryDialogOpen: (open: boolean) => void;
  setSalesDialogOpen: (open: boolean) => void;
  setDetailDialogOpen: (open: boolean) => void;
  handleCalcDialogClose: () => void;
  onCalcDialogClearAfterSuccess: () => void;
  selectedProductsForDialog: SelectedProduct[];
  selectedSupplierEstimatedAmount: number | null;
  dialogSelectionSyncLoading: boolean;
  handleDialogMonthsChange: (months: number) => void;
  forecastMonths: number;
  // Default global del campo "Crecimiento proyectado" (policy purchase_growth_pct).
  initialGrowthPct: number;
  parentCalcResults: CalcResult[];
  parentCalcLoading: boolean;
  parentSamplesByProduct: SamplesByProduct;
  parentGlobalMarginRatio: number | null;
  onRecalculate: () => void;
  confirmAction: "activate" | "deactivate" | null;
  selectedIdsSize: number;
  handleConfirmDialogClose: () => void;
  handleConfirmDialogConfirm: () => void;
  onRemoveSelectedProduct: (unifiedProductId: string) => void;
  onToggleActiveForPurchase: (unifiedProductId: string) => void;
}

export function ForecastPageDialogs(
  props: ForecastPageDialogsProps,
): React.ReactElement {
  return (
    <>
      <InventoryDetailsDialog
        open={props.inventoryDialogOpen}
        unifiedProductId={props.selectedProductId}
        productName={props.selectedProductName}
        onClose={() => props.setInventoryDialogOpen(false)}
      />
      <SalesDetailsDialog
        open={props.salesDialogOpen}
        unifiedProductId={props.selectedProductId}
        productName={props.selectedProductName}
        onClose={() => props.setSalesDialogOpen(false)}
      />
      <ProductDetailDrawer
        open={props.detailDialogOpen}
        unifiedProductId={props.selectedProductId}
        productName={props.selectedProductName}
        onClose={() => props.setDetailDialogOpen(false)}
        onToggleActiveForPurchase={props.onToggleActiveForPurchase}
      />
      <SelectedProductsDialog
        open={props.calcDialogOpen}
        onClose={props.handleCalcDialogClose}
        onCloseAfterSuccess={props.onCalcDialogClearAfterSuccess}
        products={props.selectedProductsForDialog}
        initialSuggestedTotal={props.selectedSupplierEstimatedAmount}
        selectionSyncLoading={props.dialogSelectionSyncLoading}
        onStepChange={() => {
          // Cost data now comes from canonical pipeline — no runtime refetch needed
        }}
        onMonthsChange={props.handleDialogMonthsChange}
        initialMonths={props.forecastMonths}
        initialGrowthPct={props.initialGrowthPct}
        calcResults={props.parentCalcResults}
        calcLoading={props.parentCalcLoading}
        samplesByProduct={props.parentSamplesByProduct}
        globalMarginRatio={props.parentGlobalMarginRatio}
        onRecalculate={props.onRecalculate}
        onRemoveProduct={props.onRemoveSelectedProduct}
      />

      {/* Dialog de confirmación para activar/desactivar forecast */}
      <Dialog
        open={props.confirmDialogOpen}
        onClose={props.handleConfirmDialogClose}
      >
        <DialogTitle>
          {props.confirmAction === "activate"
            ? "Activar Forecast de Compras"
            : "Desactivar Forecast de Compras"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {props.confirmAction === "activate"
              ? `¿Está seguro que desea activar el forecast de compras para ${props.selectedIdsSize} producto(s) seleccionado(s)?`
              : `¿Está seguro que desea desactivar el forecast de compras para ${props.selectedIdsSize} producto(s) seleccionado(s)?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.handleConfirmDialogClose} size="small">
            Cancelar
          </Button>
          <Button
            onClick={props.handleConfirmDialogConfirm}
            variant="contained"
            size="small"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
